package com.swetdot.swetgram.ui.design

import androidx.compose.animation.Crossfade
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * The Design System's own components.
 *
 * They exist because Material3's defaults contradict the specification in ways that remapping
 * the theme cannot reach. `Button` takes its shape from its own token -- a pill -- not from
 * `shapes.medium`, so a themed Button still arrives fully rounded where the SDS says 16dp.
 * `OutlinedTextField` enforces a 56dp minimum against the specified 52dp. Each of those is one
 * call site away from leaking back in, which is why screens should use these and never the
 * Material originals directly.
 */

private val ButtonShape = RoundedCornerShape(Radius.md)

/** What a filled button looks like when it cannot be pressed. See [swetDisabledFill]. */
internal data class SwetDisabledFill(val container: Color, val label: Color)

/**
 * The disabled treatment for a FILLED button, in tokens, opaque.
 *
 * WHAT IT REPLACES, and why the old one was not merely dim. Both filled buttons dressed their
 * disabled state as `<accent-or-danger>.copy(alpha = 0.38f)` behind `Color.White.copy(alpha = 0.7f)`
 * -- two translucent layers whose contrast is not a property of the button at all but of whatever
 * happens to be behind it. Composited onto the LIGHT `surface` the accent pair lands at roughly
 * 1.4:1, which is not low contrast: it is a white label on a pale blue pill, effectively absent.
 * It was reported on the two report sheets and it was never theirs -- every disabled primary and
 * destructive button in the app was drawn that way.
 *
 * TWO OPAQUE TOKENS, so the ratio is a fact about the pair rather than about the backdrop:
 * `card` is the neutral surface the design system already uses for a control that is present but
 * inert (`SendButton` in the composer does exactly this), and `text.tertiary` is the token
 * `SwetTextField` documents as "placeholder and disabled text only" -- it is already the disabled
 * label of `SwetSecondaryButton`, `SwetTextButton`, `SwetDestructiveTextButton` and
 * `SwetIconButton`. After this, every disabled control in the app speaks the same language.
 *
 * MEASURED, AND ONE NUMBER GOES DOWN. Light: 1.4:1 becomes about 2.3:1. Dark: about 6:1 becomes
 * about 3.3:1, because white-at-70% over a translucent blue was accidentally *legible* on a near
 * black background. That is a real cost and it is taken deliberately -- the dark figure still
 * clears WCAG 1.4.11's 3:1 floor for non-text, WCAG 1.4.3 exempts inactive controls from the text
 * ratio outright, and the alternative is keeping a treatment that is unreadable in the light theme
 * and unlike every other disabled control in the app. What tertiary CANNOT do is reach AA in the
 * light theme: #9CA3AF is a mid grey, so no light neutral behind it exceeds about 2.5:1. Closing
 * that means moving the token itself in `SwetgramColors`, which is a design decision and not this
 * change's.
 *
 * The arithmetic is pinned in `ReportFlowTest` -- the sheet this was reported on, and the only test
 * file this change was allowed to extend. It is a design-system rule and belongs beside
 * `SwetgramColorsTest` when that file is next opened.
 */
internal fun swetDisabledFill(colors: SwetgramColors): SwetDisabledFill = SwetDisabledFill(
    container = colors.card,
    label = colors.textTertiary,
)

/**
 * The label colour for anything FILLED with `danger`.
 *
 * A PURE FUNCTION FOR ONE COLOUR, and the reason is that the wrong answer shipped. This host has
 * no renderer, so a colour chosen inside a composable cannot be asserted -- the same argument
 * [swetTextButtonColor] and [swetBadgeColors] are extracted for, and the same argument this file
 * has already lost once: the destructive button's label was a raw `Color.White`, which was right
 * against the old #EF4444-era palette and became 3.19:1 the moment the monochrome pass moved
 * `danger` to the light red so it could be READ as text. It was seen on a phone, on the Delete
 * button of the chat-list confirmation, and nothing in the build had an opinion about it.
 *
 * BLACK, AND IT STAYS BLACK NOW THAT `danger` IS WHITE. The value has been right through two
 * different palettes for two different reasons, which is the argument for the function existing
 * rather than for either answer: against the light red it was the only label that cleared AA
 * (6.58:1 where white gave 3.19), and against white it is the only one that is visible at all.
 *
 * The cost of `danger` going monochrome is stated here because this is where it lands: a filled
 * DESTRUCTIVE button and a filled PRIMARY button are now the same white pill with the same black
 * label. What separates them is the word on them and where they sit -- Delete is the confirmation's
 * first action and Cancel is under it. See `SwetgramColors.danger`.
 */
internal fun swetOnDangerFill(colors: SwetgramColors): Color = colors.textInverse

/**
 * SDS §11.1: accent background, white text, 16dp radius, 48-52dp tall, 20dp side padding.
 *
 * §19.4 opens its list of what motion is FOR with "Button press feedback", so the press is a
 * `motion.instant` (80ms) scale from [swetPressScale] and the swap to `accent.blue.pressed` is
 * animated over the same 80ms rather than snapping. The default→loading swap is a `motion.fast`
 * (150ms) crossfade -- §19.2 gives that token to "Buttons, state changes", and it is what stops
 * a spinner from appearing to teleport into the middle of a word.
 */
@Composable
fun SwetPrimaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false,
    icon: ImageVector? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val disabled = swetDisabledFill(colors)
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val container by animateColorAsState(
        targetValue = if (pressed) colors.accentPressed else colors.accent,
        animationSpec = swetTween(Motion.instant, SwetEasing.enter),
        label = "primaryButtonContainer",
    )

    Button(
        onClick = onClick,
        // A loading button that still fires is how one tap becomes two registrations.
        enabled = enabled && !loading,
        shape = ButtonShape,
        interactionSource = interactionSource,
        colors = ButtonDefaults.buttonColors(
            containerColor = container,
            // A RAW COLOUR IN A FILE THAT IS OTHERWISE TOKENS ONLY, and it stays because there is
            // no token to replace it with. `SwetgramColors` has no "text on accent": `textInverse`
            // is #FFFFFF in light and #0B0B0B in DARK, so using it here would paint a near-black
            // label on the blue pill in the dark theme. `toColorScheme` writes the same literal for
            // the same reason (`onPrimary`), and `ChatColors.onAccent` is the nearest thing to a
            // name for it -- a CHAT token, wrong to reach for from the design system's buttons.
            // Inventing one is off-limits (§4.8), so this is recorded rather than papered over.
            contentColor = colors.onAccent,
            disabledContainerColor = disabled.container,
            disabledContentColor = disabled.label,
        ),
        contentPadding = PaddingValues(horizontal = 20.dp),
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.buttonHeight)
            .swetPressScale(interactionSource, enabled = enabled && !loading),
    ) {
        Crossfade(
            targetState = loading,
            animationSpec = swetTween(Motion.fast, SwetEasing.enter),
            label = "primaryButtonLoading",
        ) { isLoading ->
            if (isLoading) {
                // The label is replaced rather than hidden behind the spinner: keeping it would
                // make the button jump width when the state changes.
                CircularProgressIndicator(
                    modifier = Modifier.size(Sizes.iconMedium),
                    // The second raw colour in this file, and the same gap: it must match the
                    // label it replaces, and the label has no token. See `contentColor` above.
                    color = colors.onAccent,
                    strokeWidth = 2.dp,
                )
            } else {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    if (icon != null) {
                        SwetIcon(
                            icon = icon,
                            tint = colors.onAccent,
                            contentDescription = null,
                            size = 24.dp
                        )
                        Spacer(Modifier.width(Space.two))
                    }
                    Text(text = text, style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}

/** SDS §11.1: transparent, subtle border, primary text. Cancel, edit, retry. */
@Composable
fun SwetSecondaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors
    val interactionSource = remember { MutableInteractionSource() }
    Button(
        onClick = onClick,
        enabled = enabled,
        shape = ButtonShape,
        interactionSource = interactionSource,
        border = BorderStroke(Sizes.borderHairline, colors.border),
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.Transparent,
            contentColor = colors.textPrimary,
            disabledContainerColor = Color.Transparent,
            disabledContentColor = colors.textTertiary,
        ),
        contentPadding = PaddingValues(horizontal = 20.dp),
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.buttonHeight)
            .swetPressScale(interactionSource, enabled = enabled),
    ) {
        Text(text = text, style = MaterialTheme.typography.labelLarge)
    }
}

/**
 * SDS §11.1: no background, `text.primary` label. Forgot password, terms, learn more. The label
 * colour is [swetTextButtonColor]'s, and that function states why it is not the accent.
 *
 * [destructive] swaps the plain label for `danger` and is SDS §11.1's destructive button in the one
 * shape [SwetDestructiveTextButton] cannot take: INLINE. That component is `fillMaxWidth` by
 * construction, which is correct for a stacked full-width control at the bottom of a settings list
 * and wrong inside a comment's action row, where three labels sit 8dp apart on one line.
 *
 * It exists because the alternative was what shipped. `CommentRow`'s Delete and Report were drawn
 * with this button in the same plain label colour as the Edit sitting 8dp away, at the same weight,
 * with nothing but the word to tell them apart -- while the comment above the call site cited
 * §11.1's "Text or background: danger" and then reached for the non-destructive control. §4.6
 * assigns `danger` to "Delete, report, destructive actions" and §4.8's "use red only for
 * destructive actions" cuts both ways: it also means destructive actions must be reachable AS red,
 * or the colour stops meaning anything.
 */
@Composable
fun SwetTextButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    destructive: Boolean = false,
) {
    val colors = SwetgramThemeTokens.colors
    val interactionSource = remember { MutableInteractionSource() }
    TextButton(
        onClick = onClick,
        enabled = enabled,
        interactionSource = interactionSource,
        modifier = modifier
            .defaultMinSize(minHeight = TouchTarget.minimum)
            .swetPressScale(interactionSource, enabled = enabled),
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelLarge,
            // §22.2: the colour reinforces and never carries the meaning alone -- the label still
            // says "Delete".
            color = swetTextButtonColor(
                enabled = enabled,
                destructive = destructive,
                colors = colors,
            ),
        )
    }
}

/**
 * Which token a text button's label takes. Extracted so the rule can be asserted without a
 * renderer, which this host cannot run -- the defect it replaces was three call sites choosing the
 * wrong colour, which is exactly the kind of thing "verified by reading it" misses.
 *
 * THE ORDINARY LABEL IS `text.primary`, NOT `accent`, and that is forced by arithmetic rather than
 * taste. The accent is a FILL: it has to be dark enough to carry a white label on top of it, which
 * puts it far below the contrast a small red word needs against `card` or `surface`. No single red
 * does both jobs, so the accent does the one it was chosen for and never appears as text. This is
 * the most-drawn label in the app, so it is also the single largest instance of that rule.
 *
 * `danger` still does appear as text, and after this it is the ONLY red text in the app -- which is
 * the point of the split: red as a word now means destruction or failure and nothing else.
 *
 * Disabled beats destructive: a control that cannot be pressed should not be shouting.
 */
internal fun swetTextButtonColor(
    enabled: Boolean,
    destructive: Boolean,
    colors: SwetgramColors,
): Color = when {
    !enabled -> colors.textTertiary
    destructive -> colors.danger
    else -> colors.textPrimary
}

/**
 * SDS §11.1's destructive button in its TEXT form -- "Text or background: danger" gives both, and
 * this is the quiet one.
 *
 * It exists because the filled variant is not always the right weight. A filled danger button is
 * the loudest thing on any screen it appears on, which is correct for the confirm button INSIDE a
 * confirmation dialog -- there it is the one thing being decided -- and wrong for a permanent
 * account-level action sitting at the bottom of a settings list, where it out-shouts the routine,
 * reversible control above it and puts a first-time user's eye on the irreversible one.
 *
 * Same 44dp minimum and same confirmation requirement as the filled variant: the treatment is
 * quieter, the guardrail is not.
 */
@Composable
fun SwetDestructiveTextButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors
    val interactionSource = remember { MutableInteractionSource() }
    TextButton(
        onClick = onClick,
        enabled = enabled,
        interactionSource = interactionSource,
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.buttonHeight)
            .swetPressScale(interactionSource, enabled = enabled),
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelLarge,
            // §22.2: the colour reinforces, it is never the only signal -- the label says
            // "Delete" too.
            color = if (enabled) colors.danger else colors.textTertiary,
        )
    }
}

/**
 * THE send disc: a filled accent circle with an upward arrow, at §6.3's recommended 48dp.
 *
 * Not [SwetIconButton], which is a transparent 44dp target for chrome actions. Not
 * [SwetPrimaryButton] either, which is a full-width labelled button. This is the shape at the end
 * of a composer, and the app has two composers.
 *
 * IT WAS WRITTEN TWICE, WITH THE GEOMETRY COPIED. `ChatScreen.kt`'s private `SendButton` and
 * `CommentsSheet.kt`'s private `CommentSendButton` drew the same three lines at the same seven
 * fractions on the same 24dp canvas. Nothing would have reported the two drifting apart, and the
 * arrow is the one mark in the app that means "this sends what I just wrote" on two different
 * surfaces.
 *
 * ONE PRESS FEEDBACK FOR BOTH, and it is the conversation's [SwetPressFeedback.Scale] rather than
 * the state layer the comment disc had. §2.1 gives nothing the right to stand between the user and
 * the message they just sent, which is the argument `ChatScreen` made for its own; posting a
 * comment is the same act on a smaller stage, and two accent discs answering a finger two different
 * ways is exactly the drift a shared component exists to prevent.
 *
 * THE DISABLED STATE IS REAL HERE, unlike in the conversation, where the disc is drawn only when
 * there is something to send so "enabled" and "present" are the same fact. The comment composer
 * keeps its disc in place and greys it: the row has no second control to hold its height, so an
 * appearing disc would move the pill and the reader's own avatar on the first character typed.
 * Both behaviours are the caller's -- this component draws whichever it is told.
 *
 * `chat.onAccent` for the arrow. Never `text.primary` or `text.inverse`: each is right in one theme
 * and illegible in the other, and the accent does not change with the theme.
 */
@Composable
fun SwetSendButton(
    contentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors
    val description = contentDescription
    val arrow = if (enabled) SwetgramThemeTokens.chat.onAccent else colors.textTertiary
    val fill = if (enabled) colors.accent else colors.card

    Box(
        modifier = modifier
            .size(TouchTarget.recommended)
            .clip(CircleShape)
            .background(fill)
            .swetPressable(
                onClick = onClick,
                enabled = enabled,
                role = Role.Button,
                feedback = SwetPressFeedback.Scale,
            )
            // Without this the disc is an unlabelled node: a screen reader reaches the end of the
            // composer and finds a button with nothing to say about it.
            .semantics { this.contentDescription = description },
        contentAlignment = Alignment.Center,
    ) {
        // Decorative: the disc itself carries the description above, so a second one here would
        // have a screen reader say "Send" twice.
        SwetIcon(
            icon = SwetIcons.ArrowUp,
            tint = arrow,
            contentDescription = null,
            size = Sizes.iconMedium,
        )
    }
}

/**
 * SDS §11.1. Destructive actions are `danger`, and the SDS requires a confirmation dialog
 * around them -- this component only carries the colour; the confirmation is the caller's.
 *
 * The FILLED variant. Use it where destroying something is the whole purpose of the surface --
 * the confirm button in a confirmation dialog. For a destructive action that merely lives on a
 * screen among others, [SwetDestructiveTextButton] is the same token at the right weight.
 */
@Composable
fun SwetDestructiveButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors
    // THE SAME DISABLED RULE AS THE PRIMARY BUTTON, and deliberately not a red-tinted variant of
    // it: the translucent-danger pill had the identical defect (a white label on pale pink), and a
    // control that cannot be pressed should not be shouting `danger` either -- the rule
    // [swetTextButtonColor] already states for the text variants.
    val disabled = swetDisabledFill(colors)
    val interactionSource = remember { MutableInteractionSource() }
    Button(
        onClick = onClick,
        enabled = enabled,
        shape = ButtonShape,
        interactionSource = interactionSource,
        colors = ButtonDefaults.buttonColors(
            containerColor = colors.danger,
            // `danger` is white in the monochrome palette, so the destructive fill needs black
            // content. Keeping the pairing in `swetOnDangerFill` makes it measurable in host tests.
            contentColor = swetOnDangerFill(colors),
            disabledContainerColor = disabled.container,
            disabledContentColor = disabled.label,
        ),
        contentPadding = PaddingValues(horizontal = 20.dp),
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.buttonHeight)
            .swetPressScale(interactionSource, enabled = enabled),
    ) {
        Text(text = text, style = MaterialTheme.typography.labelLarge)
    }
}

/**
 * SDS §11.2: 52dp minimum, 16dp radius, subtle border, accent border on focus, danger border
 * plus helper text on error.
 *
 * `OutlinedTextField` is used rather than a hand-rolled field because it carries the label
 * animation, the cursor, text selection and IME handling -- all of which are a great deal of
 * work to reproduce and easy to get subtly wrong. Only its colours, shape and minimum height
 * are overridden.
 */
@Composable
fun SwetTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    /**
     * The field's persistent name -- SDS §11.2's "Label above field".
     *
     * Distinct from [placeholder], and the distinction is the whole point: a placeholder is
     * REPLACED by what the user types, so a form that relies on one is unlabelled the moment it
     * holds a value. On a form seeded with the account's current details -- Edit Profile -- that
     * is every field on first paint, and the user is left guessing which box is the username.
     *
     * `OutlinedTextField` floats this into the border when the field is focused or filled, which
     * is exactly the behaviour §11.2 describes and the reason this is a label rather than a `Text`
     * emitted above the box.
     *
     * Null keeps the bare field, for the search bars and one-line prompts where the surrounding
     * copy already names the input.
     */
    label: String? = null,
    enabled: Boolean = true,
    errorText: String? = null,
    singleLine: Boolean = true,
    visualTransformation: androidx.compose.ui.text.input.VisualTransformation =
        androidx.compose.ui.text.input.VisualTransformation.None,
    keyboardOptions: androidx.compose.foundation.text.KeyboardOptions =
        androidx.compose.foundation.text.KeyboardOptions.Default,
    trailingIcon: @Composable (() -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val isError = errorText != null

    if (usesBrowserNativeTextInput) {
        var focused by remember { mutableStateOf(false) }
        Column(modifier.fillMaxWidth()) {
            if (label != null) Text(
                label, style = MaterialTheme.typography.labelMedium,
                color = if (isError) colors.danger else colors.textSecondary,
                modifier = Modifier.padding(start = Space.two, bottom = Space.half),
            )
            Row(
                Modifier.fillMaxWidth()
                    .defaultMinSize(minHeight = TouchTarget.fieldHeight)
                    .border(1.dp, when { isError -> colors.danger; focused -> colors.accent; else -> colors.border }, RoundedCornerShape(Radius.md))
                    .padding(start = Space.two, end = Space.one, top = Space.one, bottom = Space.one),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(Modifier.weight(1f), contentAlignment = if (singleLine) Alignment.CenterStart else Alignment.TopStart) {
                    if (value.isEmpty()) Text(
                        placeholder, style = MaterialTheme.typography.bodyMedium, color = colors.textTertiary,
                    )
                    BrowserNativeTextInput(
                        value, onValueChange, Modifier.fillMaxWidth(),
                        MaterialTheme.typography.bodyMedium.copy(color = if (enabled) colors.textPrimary else colors.textTertiary),
                        enabled, singleLine, if (singleLine) 1 else 5, keyboardOptions,
                        androidx.compose.foundation.text.KeyboardActions.Default,
                        visualTransformation, label ?: placeholder, { focused = it },
                    )
                }
                trailingIcon?.invoke()
            }
            if (errorText != null) Text(
                errorText, style = MaterialTheme.typography.labelMedium, color = colors.danger,
                modifier = Modifier.padding(start = Space.two, top = Space.half),
            )
        }
        return
    }

    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        enabled = enabled,
        isError = isError,
        singleLine = singleLine,
        visualTransformation = visualTransformation,
        keyboardOptions = keyboardOptions,
        trailingIcon = trailingIcon,
        label = label?.let {
            {
                Text(
                    // §5.3's `label.medium`. Not textTertiary: that token is placeholder and
                    // disabled text only, and a label is neither -- it stays true while the field
                    // is filled.
                    text = it,
                    style = MaterialTheme.typography.labelMedium,
                    color = if (isError) colors.danger else colors.textSecondary,
                )
            }
        },
        placeholder = {
            Text(
                text = placeholder,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textTertiary,
            )
        },
        supportingText = errorText?.let {
            {
                Text(text = it, style = MaterialTheme.typography.labelMedium, color = colors.danger)
            }
        },
        textStyle = MaterialTheme.typography.bodyMedium,
        shape = RoundedCornerShape(Radius.md),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = colors.accent,
            unfocusedBorderColor = colors.border,
            errorBorderColor = colors.danger,
            disabledBorderColor = colors.border.copy(alpha = 0.5f),
            focusedTextColor = colors.textPrimary,
            unfocusedTextColor = colors.textPrimary,
            disabledTextColor = colors.textTertiary,
            cursorColor = colors.accent,
            focusedContainerColor = Color.Transparent,
            unfocusedContainerColor = Color.Transparent,
        ),
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.fieldHeight),
    )
}

/**
 * SDS §11.5. Circle, initials fallback, green dot bottom-right with a 2dp surface-coloured
 * border so it reads as separate from the avatar rather than part of the image.
 *
 * The fallback background is DETERMINISTIC -- the same person is always the same colour, in a
 * neutral range. A random bright colour would break the brand and, worse, make the same contact
 * look different on each device.
 *
 * The online dot pops in and out over `motion.fast` (150ms, §19.2's "state changes"). It matters
 * that this is animated rather than instant: presence arrives over the socket at a moment the
 * user did not act, and a green dot that simply materialises next to a face is read as a
 * rendering glitch the first two or three times somebody sees it.
 *
 * [avatarUrl] is drawn OVER the initials rather than instead of them, and that is the whole design:
 * the initials are the layer that is always there, so they are what shows while the photo is in
 * flight and what remains if it never arrives. There is no separate loading state to write and no
 * moment where the circle is empty -- which is what a skeleton in a 44dp disc would amount to.
 */
@Composable
fun SwetAvatar(
    displayName: String,
    modifier: Modifier = Modifier,
    size: Dp = Sizes.avatarMedium,
    isOnline: Boolean = false,
    surfaceColor: Color? = null,
    /** The stored photo. Null, or unreachable, leaves the deterministic initials showing. */
    avatarUrl: String? = null,
    /**
     * What the green dot says out loud -- SDS §22.4 names this string exactly ("User is online")
     * and §22.2 requires that status is never carried by colour alone.
     *
     * It defaults ON, because the dot's usual home is a list row where it is the ONLY statement
     * of presence: a chat row and a search result show the dot and nothing else, so a screen
     * reader user was getting no presence at all from either.
     *
     * NULL makes it decorative, for the two surfaces that also render a presence SENTENCE -- the
     * conversation header and the profile identity block. There the dot is a second rendering of
     * a line that is already being read, and announcing both has the reader say "Online" twice on
     * one screen.
     *
     * There is deliberately no equivalent for absence. The dot is simply not drawn when somebody
     * is not online, and it is not drawn either when their presence is hidden, so a label saying
     * "Offline" would be asserting the difference this component cannot see.
     */
    onlineContentDescription: String? = "Online",
) {
    val colors = SwetgramThemeTokens.colors
    val background = surfaceColor ?: colors.surface
    val initials = initialsOf(displayName)
    val disc = avatarBackground(displayName, colors)

    Box(modifier = modifier.size(size)) {
        Box(
            modifier = Modifier
                .size(size)
                .background(disc, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = initials,
                // Derived from `size`, the way the online dot below already is. See
                // [avatarInitialsRole]: 16sp initials in a 96dp disc read as a bug.
                style = when (avatarInitialsRole(size)) {
                    AvatarInitialsRole.LabelMedium -> MaterialTheme.typography.labelMedium
                    AvatarInitialsRole.TitleMedium -> MaterialTheme.typography.titleMedium
                    AvatarInitialsRole.HeadlineSmall -> MaterialTheme.typography.headlineSmall
                    AvatarInitialsRole.HeadlineMedium -> MaterialTheme.typography.headlineMedium
                },
                // MEASURED against the disc, not assumed to be `textPrimary`. See
                // [avatarInitialsColor] -- the assumption is what put a quarter of all names at
                // 1.13:1 in the light theme.
                color = avatarInitialsColor(disc, colors),
                textAlign = TextAlign.Center,
                // The name is already announced by the row around it; reading the initials
                // again would just be noise to a screen reader.
                modifier = Modifier.clearAndSetSemantics { },
            )

            if (!avatarUrl.isNullOrBlank()) {
                AvatarPhoto(url = avatarUrl, size = size)
            }
        }

        val dot = if (size > Sizes.avatarMedium) Sizes.onlineDotLarge else Sizes.onlineDot
        // Aliased before the lambda below, where the receiver has a property of the same name.
        val onlineDescription = onlineContentDescription
        AnimatedVisibility(
            visible = isOnline,
            modifier = Modifier.align(Alignment.BottomEnd),
            enter = swetPopIn(),
            exit = swetPopOut(),
        ) {
            Box(
                modifier = Modifier
                    .size(dot + Sizes.onlineDotBorder * 2)
                    .background(background, CircleShape)
                    .padding(Sizes.onlineDotBorder)
                    .background(colors.success, CircleShape)
                    .then(
                        if (onlineDescription != null) {
                            Modifier.semantics { contentDescription = onlineDescription }
                        } else {
                            Modifier.clearAndSetSemantics { }
                        },
                    ),
            )
        }
    }
}

// -------------------------------------------------------------------------------------------
// The group avatar: four faces in one circle
// -------------------------------------------------------------------------------------------

/**
 * One face in a [SwetGroupAvatar], as the DESIGN SYSTEM sees it.
 *
 * A pair of primitives rather than a `domain.User`, and that is deliberate: `SwetListRow`'s own KDoc
 * states the rule ("it does not know what a Chat is -- that mapping belongs to the screen"), and a
 * `User` import here would be the first time anything in `ui/design` knew about the domain. The
 * screen maps `chat.members`; this draws what it is handed.
 */
data class SwetAvatarFace(val displayName: String, val avatarUrl: String?)

/**
 * How many faces the grid holds, and how many it needs before it will draw at all.
 *
 * The same number twice, on purpose: the grid is 2x2, and a 2x2 with a hole in it is a picture of
 * missing data rather than a picture of a group. See [usesGroupAvatarGrid].
 */
const val GROUP_AVATAR_TILES: Int = 4

/**
 * Does a group's row draw the 2x2 member grid, or the ordinary single disc?
 *
 * THE TWO CASES THE MOCKUP DOES NOT COVER, DECIDED HERE AND STATED RATHER THAN GUESSED AT THE CALL
 * SITE. It describes one thing -- "For a GROUP the avatar is a 2x2 GRID of four member photographs
 * inside the same circle" -- and the data can present two situations it does not describe:
 *
 *  - **The group has an avatar of its own.** Then that wins and there is no grid. A group picture is
 *    something an owner chose; the grid is what stands in when nobody chose anything. Falling back
 *    the other way would overwrite a deliberate image with an automatic one.
 *  - **The group has fewer than four members.** Then there is no grid either, and the row draws the
 *    ordinary [SwetAvatar] on the group's title -- the app's existing deterministic initials, which
 *    is exactly what a group row draws today and what the group screens draw. A grid with one, two
 *    or three tiles filled and the rest blank was the alternative and it is worse: a blank quarter
 *    depicts nothing, so a reader has to work out whether it means "a small group" or "this app is
 *    broken", and three-member groups are common enough that the answer would be on screen
 *    permanently. NOTHING IS INVENTED TO FILL THE HOLE -- no generic silhouette, no repeated face,
 *    no placeholder that would imply a member who is not there.
 *
 * A member with no `avatarUrl` is NOT one of those cases: each tile is the existing initials
 * fallback ([avatarBackground] + [initialsOf] + [avatarInitialsColor]), so a group of four where
 * nobody has a photo draws four coloured initials rather than nothing. That fallback is reused, not
 * rewritten -- there is one deterministic-initials rule in this app and this is it.
 *
 * The reader's own face is NOT excluded from the four. The disc stands for the CONVERSATION rather
 * than for "the other people in it" -- `Chat.peer` already refuses to name an "other" for a group --
 * and excluding it would make a four-person group draw three tiles and hit the fallback above.
 */
fun usesGroupAvatarGrid(avatarUrl: String?, memberCount: Int): Boolean =
    avatarUrl.isNullOrBlank() && memberCount >= GROUP_AVATAR_TILES

/**
 * The faces the grid actually draws: the first [GROUP_AVATAR_TILES], in the order given.
 *
 * The order is the repository's, which is the server's `members` array. No sort is applied -- one
 * would have to be either alphabetical (so the picture reshuffles when somebody is renamed) or by
 * some activity the chat list does not have.
 */
fun groupAvatarFaces(faces: List<SwetAvatarFace>): List<SwetAvatarFace> =
    faces.take(GROUP_AVATAR_TILES)

/**
 * SDS §11.5's circle, filled with four members instead of one.
 *
 * A component beside [SwetAvatar] rather than a private shape inside the chat row, because the group
 * screens want the same picture and a second implementation of it would drift -- the row is simply
 * the first caller.
 *
 * The four tiles are separated by [Sizes.borderHairline] of [surfaceColor], the same trick the
 * online dot uses to sit apart from the photo behind it: without it four cropped photographs of four
 * faces read as one very confusing photograph. The whole thing is clipped to a circle afterwards, so
 * the tiles are quarters of a disc rather than four squares.
 *
 * There is NO online dot here and there is no parameter for one. Presence belongs to a person, and
 * this disc is four of them; a dot on it would be a claim about whichever member happened to land in
 * the bottom-right quarter.
 *
 * The caller decides whether a grid applies at all -- see [usesGroupAvatarGrid]. This draws what it
 * is given, and given fewer than [GROUP_AVATAR_TILES] faces it draws the tiles it has: it is the
 * caller's job not to ask, and the component's job not to invent a face to fill the gap.
 */
@Composable
fun SwetGroupAvatar(
    faces: List<SwetAvatarFace>,
    modifier: Modifier = Modifier,
    size: Dp = Sizes.avatarChatRow,
    /** What the hairline between the tiles is painted with. The list's own background. */
    surfaceColor: Color? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val gap = Sizes.borderHairline
    // Each tile takes half of what is left once the single hairline between the columns (and the
    // one between the rows) is accounted for. Computed rather than `size / 2`, which would overflow
    // the disc by exactly the gap and clip a sliver off the outer edge of all four tiles.
    val tile = (size - gap) / 2
    val drawn = groupAvatarFaces(faces)

    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(surfaceColor ?: colors.surface),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(gap)) {
            for (row in 0 until 2) {
                Row(horizontalArrangement = Arrangement.spacedBy(gap)) {
                    for (column in 0 until 2) {
                        val face = drawn.getOrNull(row * 2 + column)
                        if (face == null) {
                            // Not a placeholder: literally nothing, in the colour of the gap between
                            // the tiles. Reached only by a caller that ignored `usesGroupAvatarGrid`.
                            Spacer(Modifier.size(tile))
                        } else {
                            SwetAvatarTile(face = face, size = tile)
                        }
                    }
                }
            }
        }
    }
}

/**
 * One quarter of a [SwetGroupAvatar]: the initials fallback with the photo over it.
 *
 * The same two layers [SwetAvatar] draws and for the same reason -- the initials are what shows
 * while the photo is in flight and what remains if it never arrives, so there is no loading state
 * and no moment where the quarter is empty. It is square rather than circular because the CIRCLE is
 * the grid's, one level up.
 *
 * The type role comes from [avatarInitialsRole] against the TILE's size rather than the disc's,
 * which is what stops a 52dp group avatar drawing 16sp initials in a 25dp box.
 */
@Composable
private fun SwetAvatarTile(face: SwetAvatarFace, size: Dp) {
    val colors = SwetgramThemeTokens.colors
    val disc = avatarBackground(face.displayName, colors)

    Box(
        modifier = Modifier.size(size).background(disc),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = initialsOf(face.displayName),
            style = when (avatarInitialsRole(size)) {
                AvatarInitialsRole.LabelMedium -> MaterialTheme.typography.labelMedium
                AvatarInitialsRole.TitleMedium -> MaterialTheme.typography.titleMedium
                AvatarInitialsRole.HeadlineSmall -> MaterialTheme.typography.headlineSmall
                AvatarInitialsRole.HeadlineMedium -> MaterialTheme.typography.headlineMedium
            },
            color = avatarInitialsColor(disc, colors),
            textAlign = TextAlign.Center,
            maxLines = 1,
            // The row around the avatar already names the conversation; reading four sets of
            // initials would bury that under noise.
            modifier = Modifier.clearAndSetSemantics { },
        )
        if (!face.avatarUrl.isNullOrBlank()) {
            AvatarTilePhoto(url = face.avatarUrl, size = size)
        }
    }
}

/** [AvatarPhoto] without the circular clip: the grid clips once, around all four. */
@Composable
private fun AvatarTilePhoto(url: String, size: Dp) {
    val image = rememberSwetImage(url)
    if (image.phase != SwetImagePhase.Loaded) return

    Image(
        painter = image.painter,
        contentDescription = null,
        contentScale = ContentScale.Crop,
        modifier = Modifier
            .size(size)
            .clearAndSetSemantics { }
            .swetAppear(),
    )
}

/**
 * The stored photo, painted over the initials once it has decoded.
 *
 * Nothing is drawn while it loads or if it fails -- the initials underneath are already the right
 * answer in both cases, and drawing a spinner over them would replace a correct picture of the
 * person with a statement about the network.
 *
 * Semantics cleared: the row around an avatar already names whoever it belongs to, and the initials
 * behind this are cleared for the same reason. A `contentDescription` here would make a screen
 * reader announce the name twice on every list row.
 */
@Composable
private fun AvatarPhoto(url: String, size: Dp) {
    val image = rememberSwetImage(url)
    if (image.phase != SwetImagePhase.Loaded) return

    Image(
        painter = image.painter,
        contentDescription = null,
        // Crop, not Fit: a non-square photo scaled to fit a circle leaves bars inside the disc.
        contentScale = ContentScale.Crop,
        modifier = Modifier
            .size(size)
            .clip(CircleShape)
            .clearAndSetSemantics { }
            // §19.2's `motion.fast`, collapsing to an instant swap under `LocalReduceMotion`.
            // Without it an avatar arriving mid-scroll pops, which reads as the list glitching.
            .swetAppear(),
    )
}

/**
 * At most two characters, from the first two words. Falls back to the first character of the
 * whole string, and to nothing at all rather than crashing on an empty name -- a display name
 * can be a single emoji, and `first()` on an empty string throws.
 */
internal fun initialsOf(displayName: String): String {
    val words = displayName.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    return when {
        words.isEmpty() -> ""
        words.size == 1 -> firstCharacter(words[0]).uppercase()
        else -> (firstCharacter(words[0]) + firstCharacter(words[1])).uppercase()
    }
}

/**
 * The first CHARACTER, not the first UTF-16 unit.
 *
 * `take(1)` splits a surrogate pair, so a display name that starts with an emoji -- which the
 * contract permits, since display_name is only length-bounded -- yields half a code point. It
 * does not throw; it renders as an empty box, or as nothing at all.
 */
private fun firstCharacter(word: String): String =
    if (word.length >= 2 && word[0].isHighSurrogate() && word[1].isLowSurrogate()) {
        word.take(2)
    } else {
        word.take(1)
    }

/**
 * The neutral surfaces a fallback avatar may be painted with.
 *
 * SDS §11.5 asks for "a deterministic background color from neutral palette", and the important
 * word turned out to be PALETTE -- meaning the theme's own neutral scale, not any hex that happens
 * to look grey.
 *
 * `graphite` USED TO BE ENTRY 1 AND IS GONE, and it is worth writing down why, because the hex
 * looks perfectly neutral and nothing about the line read as wrong. `brand.graphite` #262223 is SDS
 * §4.2's BRAND constant, not a theme token: §4.3's dark table lists it as `dark.graphite`, and
 * §4.4's light table has no graphite row at all. So the same near-black was painted under the
 * initials in BOTH themes, while the initials stayed `text.primary` -- white in dark, #111827 in
 * light. In the light theme that is 1.13:1. Not low contrast: ABSENT. Roughly a quarter of all
 * display names landed there (the hash is uniform mod 4), permanently, on every screen, at every
 * size including the 96dp profile and group headers.
 *
 * THREE ENTRIES, NOT FOUR, and it is not laziness. The obvious replacement was `surface`, and it is
 * wrong for a reason the contrast arithmetic cannot see: light `surface` is #FFFFFF and the chat
 * list behind it is `background` #FAFAFA, so a quarter of all avatars would have had a disc nobody
 * could see -- legible initials floating with no circle around them, which reads as a different
 * bug rather than as a fixed one. What is left is exactly the set of neutrals that appear as REAL
 * SURFACES in both §4.3 and §4.4 and are distinguishable from `background`: card, border,
 * border.strong. A shorter palette of colours that all work beats a longer one carrying a passenger.
 *
 * §4.2's `graphite` itself is UNTOUCHED -- it is the brand hex, `toColorScheme` still points
 * `surfaceContainerHighest` at it, and other surfaces may want it. It is the AVATAR that had no
 * business reaching for a brand constant.
 */
internal fun avatarPalette(colors: SwetgramColors): List<Color> =
    listOf(colors.card, colors.border, colors.borderStrong)

/**
 * Deterministic, and deliberately drawn from the neutral surfaces rather than a colour wheel:
 * SDS §11.5 says "Do not use random bright colors that break the brand."
 */
internal fun avatarBackground(displayName: String, colors: SwetgramColors): Color {
    val palette = avatarPalette(colors)
    if (displayName.isEmpty()) return palette[0]
    val hash = displayName.fold(0) { acc, ch -> acc * 31 + ch.code }
    return palette[((hash % palette.size) + palette.size) % palette.size]
}

/**
 * The initials' colour, CHOSEN FROM THE BACKGROUND rather than fixed at `text.primary`.
 *
 * Fixing the palette (see [avatarPalette]) closes today's failure; this closes the CLASS of it. The
 * initials were `colors.textPrimary` unconditionally, so the component was asserting that every
 * entry in a list it does not own would always be dark in the dark theme and light in the light
 * one. That assertion held for exactly as long as it took one brand constant to be added to the
 * list, and it failed silently -- a wrong colour compiles, renders, and looks deliberate.
 *
 * So the foreground is MEASURED: whichever of `text.primary` and `text.inverse` has the greater
 * WCAG contrast against the disc wins. In both themes as they stand today that is `text.primary`
 * everywhere, so nothing visible changes for anybody -- which is the point. It is a floor, not a
 * new look. If a future palette edit puts a dark surface in a light theme again, the initials
 * follow it instead of disappearing.
 *
 * TWO CANDIDATES, NOT AN ARBITRARY COLOUR. §4.8 forbids inventing hues, and `text.inverse` is
 * precisely §4.3/§4.4's "Text on light/high-contrast surfaces". A computed black-or-white would be
 * off-token.
 */
internal fun avatarInitialsColor(background: Color, colors: SwetgramColors): Color =
    if (swetContrastRatio(colors.textInverse, background) >
        swetContrastRatio(colors.textPrimary, background)
    ) {
        colors.textInverse
    } else {
        colors.textPrimary
    }

/**
 * Which §5.3 type role the initials take at a given avatar size.
 *
 * THE FOLDER IS SILENT AND THIS IS A DECISION. §11.5 gives avatars a five-step size scale and says
 * nothing about the initials' type role; §5.3's scale has no avatar entry. What shipped was
 * `titleMedium` (16sp) at every size from 32dp to 96dp, so on the profile and group headers a 16sp
 * glyph sat in a 96dp disc taking about a sixth of it and reading as a mistake.
 *
 * The ladder below is derived from the avatar scale the way the online dot already is (`SwetAvatar`
 * picks `onlineDotLarge` above `avatarMedium`), and every rung is an EXISTING §5.3 role -- nothing
 * invented, no new size, no new weight. It is expressed as an enum rather than a `TextStyle` so it
 * can be asserted without a renderer, which this host cannot run.
 */
internal enum class AvatarInitialsRole { LabelMedium, TitleMedium, HeadlineSmall, HeadlineMedium }

internal fun avatarInitialsRole(size: Dp): AvatarInitialsRole = when {
    // `avatar.xs` (24dp) -- mentions and compact metadata. §5.3's `label.medium`.
    size < Sizes.avatarSmall -> AvatarInitialsRole.LabelMedium
    // `avatar.sm` (32dp) and `avatar.md` (44dp) -- comments and small rows. NOT the chat list any
    // more: the inbox moved to `avatarChatRow` (52dp), which is the next band down.
    size <= Sizes.avatarMedium -> AvatarInitialsRole.TitleMedium
    // `avatar.lg` (64dp) -- user cards. §5.3's `headline` (20sp). AND `avatarChatRow` (52dp), which
    // lands here rather than above: a 52dp disc with a photo-less name in it draws 20sp initials
    // where the 44dp row drew 16sp. That is a consequence of the mockup's larger avatar rather than
    // a decision of its own -- the mockup says nothing about initials -- and it is written down here
    // because the size that crossed the boundary is not obvious from the call site.
    size <= Sizes.avatarLarge -> AvatarInitialsRole.HeadlineSmall
    // `avatar.xl` (96dp) -- the profile and group headers. §5.3's `title.medium` (24sp).
    else -> AvatarInitialsRole.HeadlineMedium
}
